/**
 * The calendar days strip's drag ghost: the in-flight existing-item drag
 * (`DragGhostState`), its projection onto a span layer's spans, and the
 * repaint when the ghost moves, settles or reverts.
 *
 * The strip reaches this module through `onExistingGhost`, `setDragGhost` and
 * the projection helpers; this module reaches the strip only through
 * `DragGhostHost`, whose members are methods. The ghost lives in
 * `DragGhostState`, which only this module assigns (the strip reads it).
 */

import { nonnull } from "@tento-core/assert";
import { CalendarColumnCellContent } from "@tento-ui/components/calendar/calendar-column-cell-content";
import { type CalendarSubcolumnSpan } from "@tento-lona/sheets-ui/sheet/calendar-subcolumn.ts";
import { type Option } from "@tento-lona/sheets";
import { DateFragment, TimePoint } from "@tento-chrono";
import { ghostHostLayer, layersToRepaintForGhost } from "./calendar-days-strip-ghost.ts";
import { dragGhostSpanId, isLiveLayer } from "./calendar-days-strip-span-cells.ts";
import type { CalendarDaysStrip } from "./calendar-days-strip.ts";
import type { SpanContexts } from "./calendar-days-strip-span-contexts.ts";

/** What the drag ghost reaches on its strip. Methods only. */
export interface DragGhostHost {
  /** The strip's span layers and each one's last render context (written through `SpanContexts` only). */
  spanContexts(): SpanContexts;
  /** Paint one span layer synchronously from `ctx`, the ghost applied. */
  paintSpans($layout: HTMLElement, ctx: CalendarDaysStrip.SpanRenderCtx): void;
  /** Run `fn` for each live span layer, pruning layers that left the DOM. */
  forEachSpanLayer(fn: ($layout: HTMLElement, ctx: CalendarDaysStrip.SpanRenderCtx) => void): void;
}

export class DragGhostState {
  /**
   * In-flight existing-item drag, broadcast via `bindPendingEvent`. The
   * promoted/injected ghost span flows through `renderSpans` so a move/resize
   * reuses the real cell (same-day) or injects a metadata span (cross-day) —
   * see `applyGhost`. Replaces the bespoke "pending cell" for existing items;
   * the new-event create ghost still uses the pending cell.
   */
  current: Option<CalendarDaysStrip.DragGhost> = null;
}

/** Replace the in-flight ghost without a repaint: the harness's `setDragGhost`, and a retire's clear. */
export function setDragGhost(state: DragGhostState, ghost: Option<CalendarDaysStrip.DragGhost>): void {
  state.current = ghost;
}

/**
 * Effective spans with the drag ghost applied. The dragged item is promoted
 * in place when this column holds its canonical span (same-day move/resize),
 * or injected as a metadata span when its range entered this column from
 * another day (cross-day). When the range leaves this column's window it is
 * dropped by `visibleSpanEntries` (the event "follows the cursor" away).
 * `phase` `"drag"` flags the cell for the drag dim — `"settle"` on drop so the
 * settled cell shows without it; `lane` says whether this subcolumn is the
 * one that hosts the moving ghost.
 */
export function projectGhost(
  spans: readonly CalendarSubcolumnSpan[],
  ghost: Option<CalendarDaysStrip.DragGhost>,
  windowed: DateFragment.Windowed,
  phase: "drag" | "settle",
  lane: "hosts-ghost" | "other-lane",
): CalendarSubcolumnSpan[] {
  if (!ghost) return [...spans];
  if (phase === "drag") {
    // ACTIVE DRAG: keep the real event's span in its lane but render it as a
    // FAINT PLACEHOLDER (`isDragSource`), and float a SEPARATE ghost at the
    // drag position. The dragged event lives in exactly ONE subcolumn, so only
    // that subcolumn (`lane: "hosts-ghost"`) floats the moving ghost — otherwise EVERY
    // subcolumn whose day-window contains the range paints its own ghost, the
    // multi-lane "double ghost" (operator-reported 2026-06-19). Non-host
    // subcolumns still fade their source span if they happen to own it. The
    // ghost span carries a DISTINCT id so its pooled cell never
    // recycle-collides with the original's when both are in this column.
    const withSource = spans.map((s) =>
      s.id === ghost.id ? { ...s, isDragSource: true } : s,
    );
    if (lane !== "hosts-ghost" || windowed.truncate(ghost.range) == null) return withSource;
    return [
      ...withSource,
      {
        ...makeGhostSpan(ghost, windowed),
        id: dragGhostSpanId(ghost.id),
        isDragging: true,
      },
    ];
  }
  // SETTLE (drop, `phase: "settle"`): promote the original to the dropped
  // position as the no-flash bridge until `canonicalCells` repaints — the
  // floating drag-ghost is gone, so the settled cell is the real event. Only
  // the host subcolumn mints a settled ghost for a cross-day drop (same
  // single-lane rule as the active drag).
  if (spans.some((s) => s.id === ghost.id)) {
    return spans.map((s) =>
      s.id === ghost.id
        ? { ...s, start: ghost.range.start, end: ghost.range.end, isDragging: false }
        : s,
    );
  }
  if (lane === "hosts-ghost" && windowed.truncate(ghost.range) != null) {
    return [
      ...spans,
      { ...makeGhostSpan(ghost, windowed), isDragging: false },
    ];
  }
  return [...spans];
}

/** Whether `$layout` floats the single drag ghost (`ghostHostLayer`). */
export function hostsDragGhost(
  host: DragGhostHost,
  $layout: HTMLElement,
  ghost: CalendarDaysStrip.DragGhost,
): boolean {
  return ghostHostLayer(host.spanContexts().view, ghost, isLiveLayer) === $layout;
}

/** Synthetic span for a cross-day drag destination (no canonical span here),
 *  built from the dragged item's metadata. `item` is carried so the gesture
 *  continues over this column; `projectGhost` stamps `isDragging`. */
export function makeGhostSpan(
  ghost: CalendarDaysStrip.DragGhost,
  windowed: DateFragment.Windowed,
): CalendarSubcolumnSpan {
  const { item, range } = ghost;
  return {
    id: ghost.id,
    start: range.start,
    end: range.end,
    pointerEvents: "all",
    item,
    callbacks: null,
    // `SpanContentBinder` (tento-ui-js) passes its ambient `Option`.
    bindContent: (existing: HTMLElement | null | undefined): HTMLElement => {
      const $content = existing instanceof CalendarColumnCellContent
        ? existing
        : CalendarColumnCellContent.make();
      const truncated = windowed.truncate(range);
      if (truncated) {
        $content.bind({
          label: item.title || "Untitled",
          time: TimePoint.Range.fromTodRange(truncated),
          timeLabel: range.toTz(windowed.fragment.tz),
        });
      }
      $content.style.background = item.backgroundColor;
      $content.style.height = "100%";
      return $content;
    },
  };
}

/** Set/clear the in-flight existing-item ghost (broadcast by
 *  `bindPendingEvent`). */
export function onExistingGhost(
  host: DragGhostHost,
  state: DragGhostState,
  ghost: Option<CalendarDaysStrip.DragGhost>,
  revert: boolean,
): void {
  if (ghost) {
    // Mid-drag: repaint from the cached spans (no re-fetch) so the
    // promoted/injected ghost tracks the cursor every move. Only layers
    // whose projection changes repaint: the old and new host, and the
    // source layers when the drag starts (calendar-days-strip-ghost.ts).
    const previous = state.current;
    state.current = ghost;
    for (const $layout of layersToRepaintForGhost(host.spanContexts().view, previous, ghost, isLiveLayer)) {
      host.paintSpans($layout, nonnull(host.spanContexts().get($layout), "ghost repaint: a span layer's context"));
    }
    return;
  }

  const settling = state.current;
  state.current = null;
  if (!settling) return;

  // Cancelled drag (no persist will follow): repaint from the unchanged
  // cached spans, restoring the original position. The contexts were never
  // mutated mid-drag (the ghost paints as an overlay), so it still holds the
  // canonical spans — we must NOT commit the dropped position here, because
  // a cancel writes nothing, so no `canonicalCells` re-render would ever
  // reconcile a committed bridge (the event would strand at the drop spot
  // until a manual refresh).
  if (revert) {
    host.forEachSpanLayer(($layout, ctx) => host.paintSpans($layout, ctx));
    return;
  }

  // Drop (persisted): commit the dragged item at its final position into the
  // cache (optimistic) and repaint synchronously — the event settles
  // instantly, no async re-derive. The canonical re-render (driven by the
  // persist's `canonicalCells` write) reconciles by id when it lands.
  host.forEachSpanLayer(($layout, ctx) => {
    const settled: CalendarDaysStrip.SpanRenderCtx = {
      ...ctx,
      spans: projectGhost(
        ctx.spans,
        settling,
        ctx.windowed,
        "settle",
        hostsDragGhost(host, $layout, settling) ? "hosts-ghost" : "other-lane",
      ),
    };
    host.spanContexts().set($layout, settled, null);
    host.paintSpans($layout, settled);
  });
}
